import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, Calendar, MapPin, Users, Clock } from "lucide-react";
import SEO from "../components/SEO";
import InquiryForm from "../components/InquiryForm";
import ReviewsSection from "../components/ReviewsSection";
import TryRiskFreeCTA from "../components/TryRiskFreeCTA";
import { fetchSiteCamp } from "../lib/api";

export default function CampDetailPage() {
  const { slug } = useParams();
  const [camp, setCamp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetchSiteCamp(slug)
      .then(setCamp)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (!loading && (notFound || !camp)) {
    return <Navigate to="/camps" replace />;
  }
  if (loading || !camp) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <p className="text-slate-500 text-sm">Loading camp…</p>
      </section>
    );
  }

  const accent = camp.accent_color || "#C2410C";
  const secondary = camp.secondary_color || "#FFF1E8";

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: typeof window !== "undefined" ? window.location.origin + "/" : "/" },
      { "@type": "ListItem", position: 2, name: "Camps", item: typeof window !== "undefined" ? window.location.origin + "/camps" : "/camps" },
      { "@type": "ListItem", position: 3, name: camp.name, item: typeof window !== "undefined" ? window.location.origin + `/camps/${camp.slug}` : `/camps/${camp.slug}` },
    ],
  };

  return (
    <>
      <SEO
        path={`/camps/${camp.slug}`}
        title={camp.seo_title || `${camp.name} | Nexora Richmond BC`}
        description={camp.seo_description || camp.description}
        image={camp.image_url}
        schemas={[breadcrumbSchema]}
      />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundColor: secondary }}
        data-testid={`camp-hero-${camp.slug}`}
      >
        <span
          className="absolute top-0 left-0 right-0 h-1.5"
          style={{ backgroundColor: accent }}
        />
        <div
          className="absolute -top-32 -right-24 w-[34rem] h-[34rem] rounded-full blur-[120px] opacity-50 pointer-events-none"
          style={{ backgroundColor: accent }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20 pb-12 lg:pb-16">
          <nav className="text-xs text-slate-500 flex flex-wrap items-center gap-1.5">
            <Link to="/" className="hover:text-slate-700">Home</Link>
            <span>/</span>
            <Link to="/camps" className="hover:text-slate-700">Camps</Link>
            <span>/</span>
            <span className="text-slate-700 font-semibold">{camp.name}</span>
          </nav>

          <div className="mt-6 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span
                className="pill"
                style={{ backgroundColor: "#FFFFFF", color: accent }}
              >
                {camp.category || "Summer Camp"}
              </span>
              {camp.brand_label && (
                <p className="mt-2 text-xs uppercase tracking-wider font-bold text-slate-500">
                  {camp.brand_label}
                </p>
              )}
              <h1
                className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-extrabold text-slate-900 mt-5 leading-[1.04]"
                data-testid="camp-headline"
              >
                {camp.name}
              </h1>
              <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
                {camp.description}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#inquire"
                  className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-white shadow-sm transition-opacity hover:opacity-90"
                  style={{ backgroundColor: accent }}
                  data-testid="camp-hero-inquire-cta"
                >
                  Request information
                  <ArrowUpRight className="h-4 w-4" />
                </a>
                <Link to="/camps" className="btn-soft">
                  Browse all camps
                </Link>
              </div>

              <ul className="mt-8 grid sm:grid-cols-2 gap-2.5 max-w-xl">
                {(camp.highlights || []).slice(0, 4).map((h) => (
                  <li key={h} className="flex items-start gap-3 text-sm text-slate-700">
                    <span
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                      style={{ backgroundColor: "#FFFFFF" }}
                    >
                      <Check className="h-3 w-3" style={{ color: accent }} />
                    </span>
                    {h}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-5">
              <div className="relative aspect-[4/3] rounded-[2rem] overflow-hidden border border-slate-200/70 bg-white shadow-md">
                {camp.image_url ? (
                  <img src={camp.image_url} alt={camp.name} className="absolute inset-0 h-full w-full object-cover" />
                ) : (
                  <div
                    className="absolute inset-0 flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, ${secondary} 0%, #FFFFFF 80%)`,
                    }}
                  >
                    <span
                      className="font-display text-3xl font-extrabold tracking-tight px-6 text-center opacity-30"
                      style={{ color: accent }}
                    >
                      {camp.name}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meta strip */}
      <section className="border-y border-slate-200/70 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Meta icon={Users} label="Ages" value={camp.age_range || camp.ages || "All ages"} />
          <Meta icon={Calendar} label="Dates" value={camp.dates || "TBA"} />
          <Meta icon={Clock} label="Schedule" value={camp.schedule || "Full day"} />
          <Meta icon={MapPin} label="Location" value={camp.location || "Richmond BC"} />
        </div>
      </section>

      {/* Description + sessions */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-12 gap-12">
        <article className="lg:col-span-7" data-testid="camp-overview">
          <p className="eyebrow">Camp overview</p>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-3 leading-[1.08]">
            What campers will do, learn, and take home.
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">{camp.description}</p>

          {camp.highlights && camp.highlights.length > 0 && (
            <div className="mt-10">
              <p className="eyebrow text-slate-600">Every camp day includes</p>
              <ul className="mt-4 grid sm:grid-cols-2 gap-2.5">
                {camp.highlights.map((h) => (
                  <li
                    key={h}
                    className="flex items-start gap-3 rounded-2xl bg-white border border-slate-200/70 p-4"
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-50">
                      <Check className="h-3.5 w-3.5 text-[#C2410C]" />
                    </span>
                    <span className="text-sm text-slate-700 leading-relaxed">{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </article>

        <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start grid gap-6">
          {camp.sessions && camp.sessions.length > 0 && (
            <div
              className="rounded-[1.75rem] bg-slate-900 text-white p-7"
              data-testid="camp-sessions"
            >
              <p className="eyebrow text-slate-400">Weekly themes & sessions</p>
              <ul className="mt-5 grid gap-2.5 text-sm">
                {camp.sessions.map((s) => (
                  <li key={s} className="flex items-start gap-3">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/10">
                      <Check className="h-3 w-3 text-[#FF8C42]" />
                    </span>
                    <span className="leading-relaxed">{s}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#inquire"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white text-slate-900 px-5 py-3 text-sm font-bold"
                data-testid="camp-side-inquire-cta"
              >
                Ask about this camp
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          )}
        </aside>
      </section>

      {/* Reviews */}
      <ReviewsSection />

      {/* Risk-free CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <TryRiskFreeCTA
          title="Not sure if this camp fits?"
          description="Tell us your child's age and interests. We'll recommend the right camp week and answer any questions about schedule, snacks, or pickup."
          ctaLabel="Ask about this camp"
          ctaTo="/contact"
        />
      </section>

      {/* Inquiry */}
      <section
        id="inquire"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 pt-10"
        data-testid={`camp-inquire-${camp.slug}`}
      >
        <div className="rounded-[2.5rem] bg-[#FFFCF7] border border-slate-200/70 p-8 sm:p-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="eyebrow">Request information</p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-3 text-slate-900 leading-[1.08]">
              Ask about {camp.name}.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Tell us your child's age and the week you're interested in. Our team
              replies within one business day.
            </p>
          </div>
          <div className="lg:col-span-7">
            <InquiryForm
              source={`camp_landing_${camp.slug}`}
              sourcePage={`/camps/${camp.slug}`}
              programSlug={camp.slug}
              programType="camp"
              title="Camp inquiry"
              subtitle="Required fields are marked with *"
              ctaLabel="Request information"
            />
          </div>
        </div>
      </section>
    </>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-50 text-[#C2410C] shrink-0">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{label}</p>
        <p className="font-display font-bold text-slate-900 mt-0.5 text-sm">{value}</p>
      </div>
    </div>
  );
}
